import * as $ from 'jquery';
import { EventHandleSpec } from '../EventHandleSpec';
import { GestureType } from '../../gen-src/preslayer/Enums';
import { IDisposable } from '../definition/IDisposable';
import { MiscUtil } from '../utility/MiscUtil';
import { NormalizedEvent } from '../event/NormalizedEvent';
import { PseudoEventName } from '../../gen-src/preslayer/Enums';
import { TableauEventHandler } from '../TableauEventHandler';
import { ZoomScaleUtil } from '../utility/ZoomScaleUtil';

/**
* Base class of gestures object
*/
abstract class Gesture implements IDisposable {
  // The default minimum threshold, in pixels, for a gesture to move before no longer being considered a tap/press gesture.
  public static readonly DefaultSlop: number = 5;

  // The max time, in ms, allowed between two taps in order to recognize a double tap.
  public static readonly DoubleTapTime: number = 390;

  // The total time, in ms, for a PotentialPress event to be fired when holding a press down. The PotentialPress event is
  // signaled on our way to Press to allow "pre-press" animations.
  public static readonly PotentialPressTime: number = 500;

  // The total time, in ms, for a Press event to be fired when holding a press down.
  public static readonly PressTime: number = 750;

  public Config: EventHandleSpec;
  private slop: number = 5;
  protected numTouches: number = 1;
  protected type: GestureType = GestureType.Base;
  private static DataAttributePrefix: string = 'tab-';

  // Note:
  // doubleTapTimer, potentialPressTimer, pressTimer get created/deleted as needed
  protected isPotentialPress: boolean = false;
  protected isActivePress: boolean = false;
  protected pressTimer: number;
  protected doubleTapTimer: number;
  protected potentialPressTimer: number;

  /**
  * Initializes a new instance of the Gesture class
  *
  * @param {GestureEventHandleSpec} config - GestureEventHandleSpec object
  * @param {GestureType} type - name of the gesture
  * @param {boolean} hasSlop - Whether the gesture should tolerate some slow
  *  (such as touch and drag events) or if it should require exact behavior (such as hover)
  * @param {number} numTouches- # of touches
  */
  public constructor(config: EventHandleSpec, type?: GestureType, hasSlop?: boolean, numTouches?: number) {
    if (!MiscUtil.IsNullOrUndefined(type)) {
      this.type = type;
    }
    if (!MiscUtil.IsNullOrUndefined(hasSlop)) {
      this.slop = hasSlop ? Gesture.DefaultSlop : 0;
    }
    if (!MiscUtil.IsNullOrUndefined(numTouches)) {
      this.numTouches = numTouches;
    }
    this.Config = new EventHandleSpec();
    this.Update(config);
  }
  private get DataAttributeName(): string {
    return Gesture.DataAttributePrefix + this.type.toString();
  }
  protected get ZoomAdjustedSlop(): number {
    let zoomScale: number = ZoomScaleUtil.GetScaleSync();
    return Math.round(this.slop / zoomScale);
  }
  public Update(config: EventHandleSpec): void {
    if (!MiscUtil.IsNullOrUndefined(config)) {
      $.extend(this.Config, config);
      $(this.Config.DomElement).data(this.DataAttributeName, this);
    }
  }
  public abstract Start(e: NormalizedEvent): void;
  public abstract Move(e: NormalizedEvent): void;
  public abstract End(e: NormalizedEvent): void;
  public abstract Cancel(): void;
  public Fire(callbackName: PseudoEventName, evt: NormalizedEvent, param: GestureEventInfo): void {
    if (this.Config.IsHandling(callbackName)) {
      let pseudoEvent: NormalizedEvent = evt;
      $.extend(pseudoEvent, param);
      if (!MiscUtil.IsNullOrUndefined(pseudoEvent.reactEvent)) {
        $.extend(pseudoEvent.reactEvent, param);
      }
      pseudoEvent.gestureName = this.type.toString();
      pseudoEvent.callbackName = callbackName;
      let callback: (_: NormalizedEvent) => void = TableauEventHandler.GetHandler(this.Config, callbackName);
      callback(pseudoEvent);
    }
  }
  public dispose(): void {
    this.Cancel();
    $(this.Config.DomElement).removeData(this.DataAttributeName);
  }

  public static FromHtml(domElement: HTMLElement, type: GestureType): Gesture {
    return $(domElement).data(Gesture.DataAttributePrefix + type.toString());
  }
}

class GestureEventInfo {
  public pageX: number;
  public pageY: number;
  public scale: number;
  public distance: number;
  public deltaX: number;
  public deltaY: number;
  public startX: number;
  public startY: number;
  public clientX: number;
  public clientY: number;
  public deltaXSinceLast: number;
  public deltaYSinceLast: number;

  // Argh -- really want to use this but we can't use es6!!
  /*
  public constructor(init?: Partial<GestureEventInfo>) {
    Object.assign(this, init);
  }
  */
}

export { GestureEventInfo, Gesture };

